import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Sprout, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, inputCls, Btn } from "@/components/common";

const Shell = ({ title, children }) => (
  <div className="grid min-h-screen place-items-center bg-[#0B130E] px-4 font-body">
    <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl sm:p-10">
      <div className="mb-8 flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-700 text-white"><Sprout className="h-5 w-5" /></span>
        <div><p className="font-display text-lg font-bold text-slate-900">Staff Portal</p><p className="flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3 w-3" />Authorized personnel only</p></div>
      </div>
      <h1 className="mb-6 font-display text-2xl font-bold text-slate-900">{title}</h1>
      {children}
    </div>
  </div>
);

export function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  if (user) return <Navigate to="/admin/dashboard" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); nav("/admin/dashboard", { replace: true }); } catch (err) { setError(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Sign in">
      <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
        <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" data-testid="admin-login-email-input" className={inputCls} /></Field>
        <Field label="Password"><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="admin-login-password-input" className={inputCls} /></Field>
        {error && <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700" data-testid="admin-login-error">{error}</p>}
        <Btn type="submit" loading={loading} className="w-full" data-testid="admin-login-submit-button">Sign in</Btn>
        <Link to="/admin/forgot-password" className="block text-center text-sm font-semibold text-emerald-800" data-testid="admin-forgot-password-link">Forgot password?</Link>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/auth/forgot-password", { email }); setMsg(r.data.message); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Reset your password">
      {msg ? <p className="rounded-lg bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="forgot-password-message">{msg}</p> : (
        <form onSubmit={submit} className="space-y-5">
          <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email-input" className={inputCls} /></Field>
          <Btn type="submit" className="w-full" data-testid="forgot-password-submit-button">Request reset link</Btn>
        </form>
      )}
      <Link to="/admin/login" className="mt-6 block text-center text-sm font-semibold text-emerald-800">Back to sign in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) return toast.error("Password must be at least 10 characters");
    try { await api.post("/auth/reset-password", { token: params.get("token"), password: pw }); toast.success("Password updated. Please sign in."); nav("/admin/login"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-5">
        <Field label="New password" hint="At least 10 characters"><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" className={inputCls} /></Field>
        <Btn type="submit" className="w-full" data-testid="reset-password-submit-button">Update password</Btn>
      </form>
    </Shell>
  );
}
